<!-- eslint-disable vue/no-v-html -->
<!-- eslint-disable vue/no-v-text-v-html-on-component -->
<template>
  <div>
    <div class="row justify-center items-center q-gutter-md q-pb-md heading">
      <div>
        <h2 class="text-h5 text-weight-medium no-margin">
          Reviews
        </h2>
      </div>
      <q-pagination
        v-model="page"
        :max="pages"
        :disable="status == 'pending'"
        size="lg"
        direction-links
        flat
        input
      />
    </div>

    <div
      v-if="status == 'pending'"
      class="container"
    >
      <div
        v-for="item in 6"
        :key="item"
        class="item"
      >
        <q-card flat>
          <q-item>
            <q-item-section avatar>
              <q-skeleton
                type="QAvatar"
                size="40px"
              />
            </q-item-section>

            <q-item-section>
              <q-skeleton
                type="text"
                width="20%"
              />
            </q-item-section>
          </q-item>

          <q-card-section class="text-body2">
            <q-skeleton
              v-for="line in 3"
              :key="line"
              type="text"
            />
          </q-card-section>

          <q-card-actions class="justify-between">
            <div class="q-px-sm">
              <q-skeleton
                type="text"
                width="100px"
                height="14px"
              />
            </div>
            <q-rating
              :model-value="0"
              max="5"
              size="xs"
              color="secondary"
              class="q-pa-xs"
              readonly
              disabled
            />
          </q-card-actions>
        </q-card>
      </div>
    </div>

    <div v-else-if="error">
      <lazy-q-banner
        class="text-white bg-negative full-width"
        inline-actions
      >
        There was an error while fetching reviews. You can retry your request or
        reset your parameters.
        <template #action>
          <q-btn
            flat
            square
            color="white"
            label="Retry"
            @click="refresh()"
          />
          <q-btn
            flat
            square
            color="white"
            label="Reset"
            @click="page = 1"
          />
        </template>
      </lazy-q-banner>
    </div>

    <div
      v-else
      class="container"
    >
      <q-card
        v-for="item in reviews"
        :key="item.id"
        class="item"
        flat
      >
        <q-card-section>
          <div class="column q-gutter-md">
            <div>
              <div class="row items-center q-gutter-md">
                <q-avatar
                  :color="item.authorName?.length ? 'info' : 'positive'"
                  :icon="!item.authorName?.length ? mdiCheck : mdiAccountStar"
                  size="40px"
                />
                <div>
                  <div class="text-subtitle2">
                    {{ item.authorName?.length ? item.authorName : 'Verified Customer' }}
                  </div>
                  <div
                    v-if="item.authorLocation?.length"
                    class="text-caption text-grey-4"
                  >
                    {{ item.authorLocation }}
                  </div>
                </div>
              </div>
            </div>

            <div
              class="text-body1 review-content"
              v-html="paragraphize(item.content)"
            />

            <div>
              <div class="row items-center">
                <div class="text-caption text-grey-6">
                  {{ relative(item.created) }}
                </div>
                <q-space />
                <q-rating
                  :model-value="item.score"
                  max="5"
                  size="xs"
                  color="secondary"
                  class="q-pa-xs"
                  readonly
                />
              </div>
            </div>
          </div>
        </q-card-section>
      </q-card>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { mdiAccountStar, mdiCheck } from '@quasar/extras/mdi-v7';
import type { PaginatedCollection, ReviewResponse } from '~/types/ecommerce';

const { product } = useProduct()!;
const { relative } = useDateFormat();
const { paragraphize } = useFormatting();
const { $ecommerce } = useNuxtApp();

const size = 10;
const page = ref(1);

const { data, status, error, refresh } = await useAsyncData(
  `products_${product.id}_reviews`,
  () => $ecommerce.fetch<PaginatedCollection<ReviewResponse>>('reviews', {
    params: {
      filters: `ProductId==${product.id}`,
      sorts: '-CreatedDt',
      page: page.value,
      size,
    },
  }),
  {
    lazy: true,
    watch: [page],
  },
);

const total = computed(() => data.value?.totalItems ?? product.reviewCount);

const pages = computed(() => Math.ceil((total.value ?? 0) / size));

const reviews = computed(() => data.value?.items ?? []);
</script>

<style lang="scss" scoped>
.heading {
  @media (min-width: $breakpoint-md-min) {
    justify-content: space-between;
  }
}

.container {
  column-gap: 16px;
  column-count: auto;
  column-width: 600px;

  .item {
    display: grid;
    margin-bottom: 16px;
    break-inside: avoid;

    &:last-child {
      margin-bottom: 0;
    }
  }
}

.review-content {
  :last-child {
    margin-bottom: 0;
  }
}
</style>
